import type { ExecutionStatsSource } from "./executionStats";

const MS_PER_DAY = 86_400_000;

// These tiers must match the monthly ledger in orchestrator/src/usage/tines_units_usage.rs.
const COMPUTE_RATE_TIERS = [
  [100, 1],
  [500, 0.75],
  [1_000, 0.5],
  [5_000, 0.25],
  [10_000, 0.1],
  [100_000, 0.01],
  [1_000_000, 0.005],
  [Infinity, 0.0025],
] as const;

type ProjectionUsage = {
  workflowId: string | null;
  spaceId: string | null;
  source: ExecutionStatsSource | null;
  billableComputeMs: number;
  storage: number;
  egress: number;
};

function computeUnitsForWorkflowSeconds(seconds: number): number {
  let units = 0;
  let tierFloor = 0;
  for (const [upTo, rate] of COMPUTE_RATE_TIERS) {
    if (seconds <= tierFloor) break;
    units += (Math.min(seconds, upTo) - tierFloor) * rate;
    tierFloor = upTo;
  }
  return units;
}

export function startOfMonthUtc(at = Date.now()): number {
  const now = new Date(at);
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
}

// Start-of-month UTC for the month `months` whole months before the one
// containing `at`. Negative `months` steps forward; Date.UTC normalizes the
// month index, so year boundaries fall out for free.
export function startOfMonthUtcMonthsAgo(
  months: number,
  at = Date.now()
): number {
  const now = new Date(at);
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - months, 1);
}

export function projectEndOfMonthUnits(
  usageRows: readonly ProjectionUsage[],
  now = Date.now()
): number | null {
  const start = startOfMonthUtc(now);
  const nextStart = startOfMonthUtcMonthsAgo(-1, now);
  const monthMs = nextStart - start;
  const elapsedMs = Math.min(Math.max(now - start, 0), monthMs);
  if (elapsedMs < MS_PER_DAY) return null;
  const scale = monthMs / elapsedMs;
  const computeByCurve = new Map<string, number>();
  let linearUnits = 0;
  for (const row of usageRows) {
    const curve = JSON.stringify(
      row.workflowId == null ? [row.spaceId, row.source] : [row.workflowId]
    );
    computeByCurve.set(
      curve,
      (computeByCurve.get(curve) ?? 0) + row.billableComputeMs
    );
    linearUnits += row.storage + row.egress;
  }
  let projected = linearUnits * scale;
  for (const milliseconds of computeByCurve.values()) {
    projected += computeUnitsForWorkflowSeconds((milliseconds / 1000) * scale);
  }
  return projected;
}
